import * as React from 'react'
import type { ComponentProps } from 'react'

import { Box, Button, Section, Tag, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import TextInput from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import bankAccounts from 'modules/payroll_bundle/store/collections/bankAccounts'
import BankAccount from 'modules/payroll_bundle/store/models/BankAccount'
import CompanyBankAccount from 'modules/settings/lib/forms/BankAccount'

interface ModalProps {
  onClose: () => void
  account: BankAccount
  isCreate?: boolean
}

const AccountModal = ({ onClose, account, isCreate }: ModalProps) => {
  const form = React.useRef(new CompanyBankAccount(account.toJS())).current

  const onSubmit = async () => {
    // FE passes all params to avoid branching in BE
    const { success } = await asyncAction(() =>
      account.save({
        bank_name: form.get('bank_name').value,
        account: form.get('account').value,
        code: form.get('code').value,
        archived: account.attributes.archived,
      })
    )

    if (success) onClose()
  }

  const title = isCreate
    ? i18n.t('settings.edit_company.bank_accounts.create_modal.title')
    : i18n.t('settings.edit_company.bank_accounts.edit_modal.title')

  const description = isCreate
    ? i18n.t('settings.edit_company.bank_accounts.create_modal.description')
    : i18n.t('settings.edit_company.bank_accounts.edit_modal.description')

  const submit = isCreate
    ? i18n.t('settings.edit_company.bank_accounts.create_modal.button')
    : i18n.t('settings.edit_company.bank_accounts.edit_modal.button')

  return (
    <Modal
      illustration="credit-card"
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Box alignItems="center" flexDirection="row" gap="s8">
          <Button onClick={onClose} type="secondary">
            {i18n.t('settings.edit_company.bank_accounts.cancel')}
          </Button>
          <Button submit>{submit}</Button>
        </Box>
      }
    >
      <Box gap="s24">
        <TextInput
          label={i18n.t('settings.edit_company.bank_accounts.account_name')}
          field={form.get('bank_name')}
        />
        <TextInput
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('settings.edit_company.bank_accounts.account_tip')}
              icon="question"
            />
          }
          label={i18n.t('settings.edit_company.bank_accounts.account_number')}
          field={form.get('account')}
        />
        <TextInput
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('settings.edit_company.bank_accounts.code_tip')}
            />
          }
          label={i18n.t('settings.edit_company.bank_accounts.account_code')}
          field={form.get('code')}
        />
      </Box>
    </Modal>
  )
}

interface AccountProps {
  account: BankAccount
}

const Account = ({ account }: AccountProps) => {
  const [open, toggle] = useToggle(false)
  const [isModalOpen, toggleOpen] = useToggle(false)

  const onArchive = () => {
    // FE passes all params to avoid branching in BE
    return asyncAction(() =>
      account.save({
        bank_name: account.attributes.bank_name,
        account: account.attributes.account,
        code: account.attributes.code,
        archived: !account.attributes.archived,
      })
    )
  }

  const archived = account.attributes.archived
    ? i18n.t('settings.edit_company.bank_accounts.unarchive')
    : i18n.t('settings.edit_company.bank_accounts.archive')

  const options: ComponentProps<typeof PopoverContent>['options'] = [
    {
      onClick: toggleOpen(true),
      title: i18n.t('settings.edit_company.bank_accounts.edit'),
    },
    {
      onClick: onArchive,
      title: archived,
      type: 'wrong',
    },
  ]

  return (
    <>
      <Box
        width="full"
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        paddingX="s32"
        paddingY="s16"
        gap="s8"
      >
        <Box width="full" flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Box>
            <Text.Normal weight="medium">{account.attributes.bank_name}</Text.Normal>
            <Text.Normal size="200" color="grey800">
              {account.attributes.account}
            </Text.Normal>
          </Box>
          {account.attributes.archived && (
            <Tag type="grey">{i18n.t('settings.edit_company.bank_accounts.archived')}</Tag>
          )}
        </Box>
        <Popover
          attachment="top right"
          targetAttachment="bottom center"
          toggle={toggle}
          open={open}
        >
          <Button onClick={toggle(true)} type="secondary" iconName="settings" />
          <PopoverContent onClose={toggle(false)} options={options} />
        </Popover>
      </Box>
      {isModalOpen && <AccountModal onClose={toggleOpen(false)} account={account} />}
    </>
  )
}

const BankAccounts = () => {
  const [isModalOpen, toggleOpen] = useToggle(false)

  const newAccount = new BankAccount({
    archived: false,
  })
  React.useEffect(() => {
    bankAccounts.fetch()
  })

  return (
    <>
      <Section
        illustration="credit-card"
        title={i18n.t('settings.edit_company.bank_accounts.title')}
        description={i18n.t('settings.edit_company.bank_accounts.description')}
        action={{
          label: i18n.t('settings.edit_company.bank_accounts.add_new'),
          onClick: toggleOpen(true),
        }}
        paddingX="s0"
      >
        <BoxList>
          {bankAccounts.models.map((account) => (
            <Account key={account.attributes.id} account={account} />
          ))}
          {bankAccounts.length === 0 && (
            <Box width="full" paddingY="s48" alignItems="center">
              <Text.Normal size="200" color="grey600">
                {i18n.t('settings.edit_company.bank_accounts.no_accounts')}
              </Text.Normal>
            </Box>
          )}
        </BoxList>
      </Section>
      {isModalOpen && <AccountModal onClose={toggleOpen(false)} account={newAccount} isCreate />}
    </>
  )
}

export default observer(BankAccounts)
